import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Badge, Button, Divider, Group, Paper, Stack, Table, Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { IconTrash } from "@tabler/icons-react"
import { formatDate } from "../../../../../lib/format-date"
import type { IProps } from "./interfaces"

/**
 * CompanyMembersList — dumb render surface for the super-admin company
 * detail page's pending-invitations list + member roster (table on desktop,
 * stacked cards on mobile). Takes the loaded members/invites and two callbacks;
 * the add-member form and all mutations stay in the smart route
 * (routes/_auth/companies/$id.tsx) because they are coupled to the page's
 * shared form state.
 */
export function CompanyMembersList({
  members,
  pendingInvites,
  isMobile,
  onRemoveMember,
  onRevokeInvite,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()

  // Revoking an invitation is destructive (the invite link stops working), so
  // confirm first — consistent with the member-removal confirm on this page.
  const confirmRevoke = (invitationId: string, invitedEmail: string): void => {
    modals.openConfirmModal({
      title: i18n._(msg`Revoke invitation for ${invitedEmail}?`),
      children: <Text size="sm">{i18n._(msg`Their invitation link will stop working immediately.`)}</Text>,
      labels: { confirm: i18n._(msg`Revoke`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => onRevokeInvite(invitationId),
    })
  }

  return (
    <>
      {pendingInvites.length > 0 ? (
        <Stack gap="xs" mb="md">
          <Text size="sm" fw={600} c="dimmed">
            <Trans>Pending invitations</Trans>
          </Text>
          {pendingInvites.map((inv) => (
            <Group key={inv.id} justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Text size="sm" truncate>
                  {inv.email}
                </Text>
                <Badge variant="light" size="sm">
                  {inv.role}
                </Badge>
              </Group>
              <Button
                size="compact-xs"
                variant="subtle"
                color="danger"
                onClick={() => confirmRevoke(inv.id, inv.email)}
              >
                <Trans>Revoke</Trans>
              </Button>
            </Group>
          ))}
          <Divider mt="xs" />
        </Stack>
      ) : null}

      {members.length > 0 ? (
        isMobile ? (
          <Stack gap="sm">
            {members.map((m) => (
              <Paper key={m.id} withBorder p="sm" radius="md">
                <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
                  <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={600} size="sm" truncate>
                        {m.user?.name || "—"}
                      </Text>
                      <Badge variant="light" size="sm">
                        {m.role}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed" truncate>
                      {m.user?.email || "—"}
                    </Text>
                    <Text size="xs" c="dimmed">
                      <Trans>Joined</Trans> {formatDate(i18n, m.createdAt)}
                    </Text>
                  </Stack>
                  <ActionIcon
                    variant="subtle"
                    color="danger"
                    aria-label={i18n._(msg`Remove member`)}
                    onClick={() => onRemoveMember(m.id, m.user?.email)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Trans>Name</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Email</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Role</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Joined</Trans>
                </Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {members.map((m) => (
                <Table.Tr key={m.id}>
                  <Table.Td>{m.user?.name || "—"}</Table.Td>
                  <Table.Td>{m.user?.email || "—"}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="sm">
                      {m.role}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{formatDate(i18n, m.createdAt)}</Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="danger"
                      onClick={() => onRemoveMember(m.id, m.user?.email)}
                    >
                      <Trans>Remove</Trans>
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )
      ) : (
        <Text c="dimmed" size="sm">
          <Trans>No members yet.</Trans>
        </Text>
      )}
    </>
  )
}
